import { createFileRoute, Link as RouterLink } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { Button, ICON_STROKE, PageHeader, Skeleton, Tag } from "@optymyse/suite-ui";
import { edits } from "../../shared/schemas/workspaces";
import { useContact } from "../lib/directory";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace } from "../lib/workspaces";
import { strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { DirectoryProblem, None, Page, Panel, Properties, Property } from "../components/directory/Page";
import { History } from "../components/directory/History";

/** Where a way to reach someone goes: a mail or a call, or nowhere for anything else. */
function hrefOf(kind: "email" | "phone" | "other", value: string): string | null {
  if (kind === "email") return `mailto:${value}`;
  if (kind === "phone") return `tel:${value.replace(/[^\d+]/g, "")}`;
  return null;
}

/**
 * A contact: their names, the ways to reach them, each with its label and which is primary, the customers they're
 * linked to and what they are there, and what happened to them, newest first, each read live.
 */
function ContactPage() {
  const { contactId } = Route.useParams();
  const view = useWorkspace(workspaceSlug());
  const contact = useContact(contactId);
  const mayEdit = view.data ? edits(view.data.role) : false;
  const breadcrumb = [{ label: strings.contacts.title, href: "/admin/contacts" }];

  if (contact.isPending)
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </Page>
    );
  if (contact.isError) {
    const refusal = contact.error instanceof ApiError ? contact.error : null;
    const gone = refusal !== null && (refusal.status === 404 || refusal.status === 410);
    return (
      <Page>
        <PageHeader breadcrumb={[...breadcrumb, { label: strings.contacts.title }]} title={strings.contacts.title} />
        {gone ? (
          <p>{refusal?.message || strings.contacts.page.gone}</p>
        ) : (
          <DirectoryProblem {...(refusal ? { message: refusal.message } : {})} onRetry={() => void contact.refetch()} />
        )}
      </Page>
    );
  }

  const record = contact.data;
  const primaryCompany = record.companies.find((link) => link.is_primary) ?? record.companies[0];
  return (
    <Page>
      <PageHeader
        breadcrumb={[...breadcrumb, { label: record.display_name }]}
        title={record.display_name}
        {...(primaryCompany
          ? { description: [primaryCompany.title, primaryCompany.company_name].filter(Boolean).join(", ") }
          : {})}
        actions={
          mayEdit ? (
            <Button asChild variant="secondary">
              <RouterLink to="/admin/contacts/$contactId/edit" params={{ contactId }}>
                <Pencil aria-hidden strokeWidth={ICON_STROKE} />
                {strings.contacts.page.edit}
              </RouterLink>
            </Button>
          ) : undefined
        }
      />
      <Panel title={strings.contacts.page.names}>
        <Properties>
          <Property label={strings.contacts.page.givenName}>{record.given_name ?? <None />}</Property>
          <Property label={strings.contacts.page.familyName}>{record.family_name ?? <None />}</Property>
          <Property label={strings.contacts.page.preferredName}>{record.preferred_name ?? <None />}</Property>
        </Properties>
      </Panel>
      <Panel title={strings.contacts.page.methods}>
        {record.methods.length === 0 ? (
          <p className="text-ink-muted">{strings.contacts.page.noMethods}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-line" aria-label={strings.contacts.page.methods}>
            {record.methods.map((method, index) => {
              const href = hrefOf(method.kind, method.value);
              return (
                // A method's id changes at every save, so its place in the list is its key.
                <li key={index} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0">
                  <span className="w-20 text-secondary text-ink-muted">{strings.contacts.kinds[method.kind]}</span>
                  {href ? (
                    <a href={href} className="break-all text-accent-text underline-offset-2 hover:underline">
                      {method.value}
                    </a>
                  ) : (
                    <span className="break-all">{method.value}</span>
                  )}
                  {method.label && <span className="text-secondary text-ink-muted">{method.label}</span>}
                  {method.is_primary && <Tag>{strings.contacts.primary}</Tag>}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
      <Panel title={strings.contacts.page.companies}>
        {record.companies.length === 0 ? (
          <p className="text-ink-muted">{strings.contacts.page.noCompanies}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-line" aria-label={strings.contacts.page.companies}>
            {record.companies.map((link) => (
              <li key={link.company_id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
                <span className="flex flex-wrap items-center gap-2">
                  <RouterLink
                    to="/admin/customers/$companyId"
                    params={{ companyId: link.company_id }}
                    className="font-medium text-ink underline-offset-2 hover:underline"
                  >
                    {link.company_name}
                  </RouterLink>
                  {link.is_primary && <Tag>{strings.contacts.primary}</Tag>}
                </span>
                <span className="text-secondary text-ink-muted">
                  {[link.title, link.department, link.relationship].filter(Boolean).join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <History kind="contact" id={contactId} />
    </Page>
  );
}

export const Route = createFileRoute("/admin/contacts/$contactId/")({ component: ContactPage });
